import React, { FC, Fragment } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel } from '@mui/material';
import { Order } from 'models/order';
import { useTranslation } from 'react-i18next';
import DeliveryScheduleTableItem from 'components/delivery-schedule/delivery-schedule-table-item.component';
import useOrderSort, { OrderSortColumn } from 'components/delivery-schedule/use-order-sort.hook';

// DD-3463: optional multi-select for the batch actions; absent on lists without them.
export type DeliveryScheduleSelectionProps = {
    batchActionSelected?: boolean;
    checkedOrderIds?: Set<string>;
    isAtLimit?: boolean;
    onHandleCheckedOrders?: (order: Order, checked: boolean) => void;
};

type DeliveryScheduleTableProps = DeliveryScheduleSelectionProps & {
    orders: Order[];
    onSentByClinicDialog?: (order: Order) => void;
    onRemoveFromScheduleDialog?: (order: Order) => void;
    onChangeInternalDeliveryDate?: (order: Order) => void;
    onChangeExternalDeliveryDate?: (order: Order) => void;
    isExternal: boolean;
};

const DeliveryScheduleTable: FC<DeliveryScheduleTableProps> = ({
    orders,
    onSentByClinicDialog,
    onRemoveFromScheduleDialog,
    onChangeInternalDeliveryDate,
    onChangeExternalDeliveryDate,
    isExternal,
    batchActionSelected,
    checkedOrderIds,
    isAtLimit,
    onHandleCheckedOrders,
}: DeliveryScheduleTableProps) => {
    const { t: translate } = useTranslation(['common', 'orders', 'delivery-schedule']);
    const { sortedOrders, sortColumn, sortDirection, handleSort } = useOrderSort(orders);

    const buildSortLabel = (column: OrderSortColumn, label: string) => (
        <TableSortLabel
            active={sortColumn === column}
            direction={sortColumn === column ? sortDirection : 'asc'}
            onClick={() => handleSort(column)}
        >
            {label}
        </TableSortLabel>
    );

    return (
        <Fragment>
            {orders.length ? (
                <TableContainer>
                    <Table sx={{ minWidth: 650 }} size="small">
                        <TableHead>
                            <TableRow>
                                {batchActionSelected ? <TableCell padding="checkbox" /> : undefined}
                                <TableCell>{translate('common:ORDER_ID')}</TableCell>
                                <TableCell sortDirection={sortColumn === 'patient' ? sortDirection : false}>
                                    {buildSortLabel('patient', translate('common:PATIENT'))}
                                </TableCell>
                                <TableCell sortDirection={sortColumn === 'destination' ? sortDirection : false}>
                                    {buildSortLabel('destination', translate('delivery-schedule:DELIVERY_TO'))}
                                </TableCell>
                                <TableCell sortDirection={sortColumn === 'dentist' ? sortDirection : false}>
                                    {buildSortLabel('dentist', translate('common:DENTIST'))}
                                </TableCell>
                                <TableCell sortDirection={sortColumn === 'deliveryDate' ? sortDirection : false}>
                                    {buildSortLabel('deliveryDate', translate('orders:DELIVERY_DATE'))}
                                </TableCell>
                                {isExternal && (
                                    <TableCell
                                        sortDirection={sortColumn === 'externalDeliveryDate' ? sortDirection : false}
                                    >
                                        {buildSortLabel(
                                            'externalDeliveryDate',
                                            translate('orders:EXTERNAL_DELIVERY_DATE'),
                                        )}
                                    </TableCell>
                                )}
                                <TableCell sortDirection={sortColumn === 'workflowStep' ? sortDirection : false}>
                                    {buildSortLabel('workflowStep', translate('orders:WORKFLOW_STEP'))}
                                </TableCell>
                                <TableCell>{translate('orders:CONSTRUCTIONS')}</TableCell>
                                <TableCell></TableCell>
                                <TableCell></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedOrders.map((order: Order) => (
                                <DeliveryScheduleTableItem
                                    key={order._id}
                                    order={order}
                                    onSentByClinicDialog={onSentByClinicDialog}
                                    onRemoveFromScheduleDialog={onRemoveFromScheduleDialog}
                                    onChangeInternalDeliveryDate={onChangeInternalDeliveryDate}
                                    onChangeExternalDeliveryDate={onChangeExternalDeliveryDate}
                                    isExternal={isExternal}
                                    batchActionSelected={batchActionSelected}
                                    checked={checkedOrderIds?.has(order._id as string)}
                                    isAtLimit={isAtLimit}
                                    onHandleCheckedOrders={onHandleCheckedOrders}
                                />
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            ) : (
                translate('orders:NO_ORDER_FOUND')
            )}
        </Fragment>
    );
};

export default DeliveryScheduleTable;
